import type { Dispatch, SetStateAction } from 'react'
import { Icon } from '@iconify/react'
import type { CampaignUpsertInput, PublicCampaignTemplateOption } from '@/lib/campaigns'
import { CAMPAIGN_SOCIAL_LINK_FIELDS } from '@/lib/campaign-social-links'
import { ContainerTabPanel } from '@/components/ContainerTabs'
import { CampaignRichTextField } from '@/components/CampaignRichTextField'
import { CampaignImageUploadField } from '@/components/CampaignImageUploadField'
import { PublicCampaignTemplatePicker } from '@/components/PublicCampaignTemplatePicker'
import { getPublicCampaignTemplateDefinition } from '@/features/publicCampaignTemplates/registry'
import { Template3CampaignFields } from '../Template3CampaignFields'
import { CampaignCustomDomainSection } from '../CampaignCustomDomainSection'
import { CampaignWhatsAppLinkField } from './CampaignWhatsAppLinkField'
import { campaignTemplateCapabilities } from './campaignEditorNavigation'
import styles from '../CampanhaEditPage.module.css'
import editorStyles from './CampaignEditor.module.css'

type Props = {
  form: CampaignUpsertInput
  setForm: Dispatch<SetStateAction<CampaignUpsertInput>>
  setSubmitError: (message: string | null) => void
  publicTemplateOptions: PublicCampaignTemplateOption[]
  existingShareImageUrl: string | null
  imageAccept: string
  maxImageBytes: number
  validateFileSize: (file: File, maxBytes: number, label: string) => string | null
  normalizeHttpsUrl: (value: string) => string
  onNavigate: (tab: string) => void
}

export function CampaignPublicPageFields({
  form,
  setForm,
  setSubmitError,
  publicTemplateOptions,
  existingShareImageUrl,
  imageAccept,
  maxImageBytes,
  validateFileSize,
  normalizeHttpsUrl,
  onNavigate,
}: Props) {
  const capabilities = campaignTemplateCapabilities(form.public_template_key)
  const template = getPublicCampaignTemplateDefinition(form.public_template_key)
  return (
    <>
      <ContainerTabPanel value="template">
        <section className={styles.editSection} aria-labelledby="section-template">
          <h2 id="section-template" className={styles.editSectionTitle}>
            Escolha o modelo da página
          </h2>
          <div className={styles.editSectionBody}>
            <div className={styles.editSubsection}>
              <div className={styles.editGridWide}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>
                    Escolha o modelo da página
                    <span className={styles.editLabelHint}>
                      (Você verá apenas os campos usados pelo modelo escolhido.)
                    </span>
                  </label>
                  <PublicCampaignTemplatePicker
                    options={publicTemplateOptions.map((option) => ({
                      ...option,
                      description: getPublicCampaignTemplateDefinition(option.key)
                        .description,
                    }))}
                    selectedKey={form.public_template_key ?? 'default'}
                    onChange={(templateKey) =>
                      setForm((prev) => ({ ...prev, public_template_key: templateKey }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className={editorStyles.templateSummary}>
              <div>
                <h3>{template.name} selecionado</h3>
                <p>
                  {capabilities.isTemplate3
                    ? 'Página com foto de fundo, apresentação, vantagens e dúvidas frequentes. As outras etapas de participação usam o Template 1.'
                    : 'Página com galeria de imagens, avaliações e detalhes expansíveis. Personalize os textos e a identidade visual nas próximas abas.'}
                </p>
              </div>
              <button
                type="button"
                className={editorStyles.action}
                onClick={() => onNavigate('pagina-publica')}
              >
                Preencher conteúdo
              </button>
            </div>
            {capabilities.isTemplate3 && (
              <p className={editorStyles.note}>
                Este modelo exige o preenchimento das seções exclusivas. Acesse{' '}
                <button
                  type="button"
                  className={editorStyles.inlineAction}
                  onClick={() => onNavigate('conteudo-template')}
                >
                  Seções do Template 3
                </button>{' '}
                para completar os textos e imagens.
              </p>
            )}
          </div>
        </section>
      </ContainerTabPanel>
      <ContainerTabPanel value="pagina-publica">
        <section className={styles.editSection} aria-labelledby="section-pagina-publica">
          <h2 id="section-pagina-publica" className={styles.editSectionTitle}>
            Textos que aparecem na página
          </h2>
          <div className={styles.editSectionBody}>
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>Apresentação da campanha</h3>
              <p className={styles.editSubsectionLead}>
                Apresente o prêmio ou a causa em poucas palavras. Use a descrição para
                explicar o que torna esta campanha especial.
              </p>
              <div className={styles.editGrid2}>
                <div className={styles.editField}>
                  <label className={styles.editLabel} htmlFor="campaign-public-title">
                    Título público
                    <span className={styles.editLabelHint}>
                      (Título principal da página da campanha)
                    </span>
                  </label>
                  <CampaignRichTextField
                    id="campaign-public-title"
                    variant="inline"
                    placeholder="Ex: Jeep Compass ou 75 mil no Pix"
                    aria-label="Título público"
                    value={form.title ?? ''}
                    onChange={(html) => setForm((prev) => ({ ...prev, title: html }))}
                  />
                </div>
              </div>

              <div className={styles.editGridWide}>
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor="campaign-public-description"
                  >
                    Descrição da campanha
                    <span className={styles.editLabelHint}>
                      Resumo da campanha. Prefira frases curtas e explique o objetivo da
                      participação.
                    </span>
                  </label>
                  <CampaignRichTextField
                    id="campaign-public-description"
                    variant="block"
                    placeholder="Ex.: Participe e ajude nossa instituição a ampliar o atendimento às famílias."
                    aria-label="Descrição da campanha"
                    value={form.description ?? ''}
                    onChange={(html) =>
                      setForm((prev) => ({ ...prev, description: html }))
                    }
                    minHeight={120}
                  />
                </div>
              </div>
            </div>
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>
                Informações próximas à compra
              </h3>
              <p className={styles.editSubsectionLead}>
                Complemente a apresentação com mensagens curtas. Preencha apenas os
                destaques que fizerem sentido para a campanha.
              </p>
              <div className={`${styles.editGrid2} ${styles.alignedFieldsGrid}`}>
                {capabilities.customCta && (
                  <div className={styles.alignedField}>
                    <label className={styles.editLabel} htmlFor="campaign-public-cta">
                      Texto do botão principal
                    </label>
                    <CampaignRichTextField
                      id="campaign-public-cta"
                      variant="inline"
                      placeholder="Ex: Participar Agora"
                      aria-label="Texto do botão principal"
                      aria-describedby="campaign-public-cta-help"
                      minHeight={88}
                      value={form.cta_text ?? ''}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, cta_text: html }))
                      }
                    />
                    <p id="campaign-public-cta-help" className={styles.fieldHelp}>
                      Texto dos botões de participação do Template 3. Use uma chamada
                      curta, como “Participar agora”.
                    </p>
                  </div>
                )}
                <div className={styles.alignedField}>
                  <label className={styles.editLabel} htmlFor="campaign-notice">
                    Aviso curto da compra
                  </label>
                  <CampaignRichTextField
                    id="campaign-notice"
                    variant="block"
                    placeholder="Ex: Em cada compra você recebe um giro da sorte!"
                    aria-label="Aviso curto da compra"
                    aria-describedby="campaign-notice-help"
                    value={form.public_notice_text ?? ''}
                    onChange={(html) =>
                      setForm((prev) => ({ ...prev, public_notice_text: html }))
                    }
                    minHeight={88}
                  />
                  <p id="campaign-notice-help" className={styles.fieldHelp}>
                    Mensagem exibida abaixo dos botões de ação. Use esse espaço para uma
                    orientação breve ao participante.
                  </p>
                </div>
                {capabilities.reviews && (
                  <div className={styles.alignedField}>
                    <label className={styles.editLabel} htmlFor="campaign-reviews-text">
                      Texto das avaliações
                    </label>
                    <CampaignRichTextField
                      id="campaign-reviews-text"
                      variant="inline"
                      placeholder="Ex: 4,9 (1.847 avaliações)"
                      aria-label="Texto das avaliações"
                      aria-describedby="campaign-reviews-help"
                      minHeight={88}
                      value={form.public_reviews_text ?? ''}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, public_reviews_text: html }))
                      }
                    />
                    <p id="campaign-reviews-help" className={styles.fieldHelp}>
                      Informe o resumo das avaliações. Exemplo: 4,9 (1.847 avaliações).
                    </p>
                  </div>
                )}
                {capabilities.reviews && (
                  <div className={styles.alignedField}>
                    <label
                      className={styles.editLabel}
                      htmlFor="campaign-live-badge-text"
                    >
                      Selo de destaque
                    </label>
                    <CampaignRichTextField
                      id="campaign-live-badge-text"
                      variant="inline"
                      placeholder="Ex: ... pessoas comprando agora"
                      aria-label="Texto do badge de destaque"
                      aria-describedby="campaign-live-badge-help"
                      minHeight={88}
                      value={form.public_live_badge_text ?? ''}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, public_live_badge_text: html }))
                      }
                    />
                    <p id="campaign-live-badge-help" className={styles.fieldHelp}>
                      Frase curta exibida ao lado das avaliações.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {capabilities.detailedContent && (
              <div className={styles.editSubsection}>
                <h3 className={styles.editSubsectionTitle}>Conteúdo formatado</h3>
                <div className={styles.editGridWide}>
                  <div className={styles.editField}>
                    <label
                      className={styles.editLabel}
                      htmlFor="campaign-awarded-titles-html"
                    >
                      Títulos premiados
                      <span className={styles.editLabelHint}>
                        (Texto exibido quando o visitante abre Títulos premiados)
                      </span>
                    </label>
                    <CampaignRichTextField
                      id="campaign-awarded-titles-html"
                      variant="block"
                      placeholder="Títulos premiados da campanha"
                      aria-label="Títulos premiados"
                      value={form.public_awarded_titles_html ?? ''}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, public_awarded_titles_html: html }))
                      }
                      minHeight={180}
                    />
                  </div>
                  <div className={styles.editField}>
                    <label
                      className={styles.editLabel}
                      htmlFor="campaign-public-details-html"
                    >
                      Descrição detalhada
                      <span className={styles.editLabelHint}>
                        (Texto da seção de detalhes, que o visitante pode expandir)
                      </span>
                    </label>
                    <CampaignRichTextField
                      id="campaign-public-details-html"
                      variant="block"
                      placeholder="Descrição detalhada da campanha"
                      aria-label="Descrição detalhada"
                      value={form.public_details_html ?? ''}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, public_details_html: html }))
                      }
                      minHeight={220}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </ContainerTabPanel>
      <ContainerTabPanel value="conteudo-template">
        <section
          className={styles.editSection}
          aria-labelledby="section-conteudo-template"
        >
          <h2 id="section-conteudo-template" className={styles.editSectionTitle}>
            Seções do Template 3
          </h2>
          <div className={styles.editSectionBody}>
            {capabilities.isTemplate3 ? (
              <Template3CampaignFields
                form={form}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                onFileError={setSubmitError}
                imageAccept={imageAccept}
                validateFileSize={validateFileSize}
                maxImageBytes={maxImageBytes}
              />
            ) : (
              <p>As seções exclusivas aparecem quando o Template 3 está selecionado.</p>
            )}
          </div>
        </section>
      </ContainerTabPanel>
      <ContainerTabPanel value="links">
        <section className={styles.editSection} aria-labelledby="section-links">
          <h2 id="section-links" className={styles.editSectionTitle}>
            Contato e links
          </h2>
          <div className={styles.editSectionBody}>
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>Contato com os participantes</h3>
              <CampaignWhatsAppLinkField
                value={form.whatsapp_button_url ?? ''}
                onChange={(link) =>
                  setForm((prev) => ({
                    ...prev,
                    whatsapp_button_url: link,
                    public_contact_url: link,
                  }))
                }
              />
            </div>
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>Redes sociais</h3>
              <p className={styles.editSubsectionLead}>
                Cole o link completo dos perfis que deseja divulgar no rodapé. Os campos
                vazios não precisam ser preenchidos.
              </p>
              <div className={styles.editGrid2}>
                {CAMPAIGN_SOCIAL_LINK_FIELDS.map((field) => (
                  <div key={field.key} className={styles.editField}>
                    <label
                      className={styles.editLabel}
                      htmlFor={`campaign-social-${field.key}`}
                    >
                      {field.label}
                    </label>
                    <input
                      id={`campaign-social-${field.key}`}
                      type="url"
                      className={styles.editInput}
                      placeholder={field.placeholder}
                      value={form.public_social_links?.[field.key] ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          public_social_links: {
                            ...(prev.public_social_links ?? {}),
                            [field.key]: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </ContainerTabPanel>
      <ContainerTabPanel value="compartilhamento">
        <section
          className={styles.editSection}
          aria-labelledby="section-compartilhamento"
        >
          <h2 id="section-compartilhamento" className={styles.editSectionTitle}>
            Como o link da campanha será apresentado
          </h2>
          <div className={styles.editSectionBody}>
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>Título e descrição do link</h3>
              <p className={styles.editSubsectionLead}>
                Personalize os textos exibidos ao compartilhar a campanha. Estas
                informações ajudam o visitante a reconhecer o conteúdo antes de abrir a
                página.
              </p>
              <div className={styles.editGrid2}>
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor="campaign-public-page-title"
                  >
                    Título da aba do navegador
                    <span className={styles.editLabelHint}>
                      (Opcional. Se vazio, o sistema usa o nome da campanha)
                    </span>
                  </label>
                  <input
                    id="campaign-public-page-title"
                    type="text"
                    className={styles.editInput}
                    maxLength={255}
                    placeholder="Ex: BMW 500K A — Campanha oficial"
                    value={form.public_page_title ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, public_page_title: e.target.value }))
                    }
                  />
                </div>
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor="campaign-public-share-title"
                  >
                    Título do compartilhamento
                    <span className={styles.editLabelHint}>
                      (Texto principal exibido ao compartilhar o link)
                    </span>
                  </label>
                  <input
                    id="campaign-public-share-title"
                    type="text"
                    className={styles.editInput}
                    maxLength={255}
                    placeholder="Ex: Participe da campanha BMW 500K A"
                    value={form.public_share_title ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, public_share_title: e.target.value }))
                    }
                  />
                  <span className={styles.characterCount}>
                    {(form.public_share_title ?? '').length}/255 caracteres
                  </span>
                </div>
              </div>
              <div className={styles.editGridWide}>
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor="campaign-public-share-description"
                  >
                    Descrição do compartilhamento
                    <span className={styles.editLabelHint}>
                      (Resumo exibido em WhatsApp e outras redes ao compartilhar)
                    </span>
                  </label>
                  <textarea
                    id="campaign-public-share-description"
                    className={styles.editTextarea}
                    rows={4}
                    maxLength={320}
                    placeholder="Ex: Confira os detalhes da campanha, veja o prêmio e participe agora mesmo."
                    value={form.public_share_description ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        public_share_description: e.target.value,
                      }))
                    }
                  />
                  <span className={styles.characterCount}>
                    {(form.public_share_description ?? '').length}/320 caracteres
                  </span>
                </div>
              </div>
              <div className={styles.editMediaGrid}>
                <CampaignImageUploadField
                  label="Imagem do compartilhamento"
                  hint="(Opcional. Se vazio, o sistema usa a imagem principal da campanha)"
                  emptyHint="PNG, JPG, WEBP ou GIF, até 5MB"
                  accept={imageAccept}
                  maxBytes={maxImageBytes}
                  validateFileSize={validateFileSize}
                  file={form.public_share_image ?? null}
                  imageUrl={existingShareImageUrl}
                  onFileChange={(nextFile) =>
                    setForm((prev) => ({ ...prev, public_share_image: nextFile }))
                  }
                  onFileError={setSubmitError}
                  previewAlt="Prévia da imagem de compartilhamento"
                  inputId="campaign-public-share-image"
                  validationLabel="A imagem de compartilhamento"
                />
              </div>
            </div>
          </div>
        </section>
      </ContainerTabPanel>
      <ContainerTabPanel value="dominio-personalizado">
        <section className={styles.editSection} aria-labelledby="section-endereco">
          <h2 id="section-endereco" className={styles.editSectionTitle}>
            Endereço da campanha
          </h2>
          <div className={styles.editSectionBody}>
            <div className={styles.editField}>
              <label className={styles.editLabel} htmlFor="campaign-public-slug">
                Endereço curto da campanha
                <span className={styles.editLabelHint}>
                  (Use letras minúsculas, números e hífens. Ex.: campanha-solidaria)
                </span>
              </label>
              <input
                id="campaign-public-slug"
                type="text"
                className={styles.editInput}
                placeholder="Ex: jeep-compass-75-mil-pix"
                value={form.slug ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              />
            </div>
            {form.slug?.trim() && (
              <div className={styles.publicPagePreview}>
                <div className={styles.publicPageInfo}>
                  <Icon icon="mdi:web" className={styles.publicPageIcon} />
                  <div className={styles.publicPageDetails}>
                    <span className={styles.publicPageLabel}>URL da página pública:</span>
                    <span className={styles.publicPageUrl}>
                      /campanha/{form.slug.trim()}
                    </span>
                  </div>
                </div>
                <a
                  href={`/campanha/${form.slug.trim()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.publicPageButton}
                >
                  <Icon icon="mdi:open-in-new" />
                  Abrir página pública
                </a>
              </div>
            )}
            <CampaignCustomDomainSection
              value={form.custom_domain ?? ''}
              publicPageUrl={form.public_page_url ?? ''}
              onChange={(customDomain) =>
                setForm((prev) => ({ ...prev, custom_domain: customDomain }))
              }
            />
            <div className={styles.editSubsection}>
              <h3 className={styles.editSubsectionTitle}>Redirecionamento externo</h3>
              <div
                className={styles.importantNote}
                role="note"
                style={{ marginBottom: '1rem' }}
              >
                <Icon icon="mdi:open-in-new" aria-hidden />
                <p>
                  <strong>Este link substitui a página da campanha.</strong> Ao
                  preenchê-lo, os visitantes serão encaminhados para o site informado.
                  Deixe vazio para usar a página criada aqui.
                </p>
              </div>
              <div className={styles.editGridWide}>
                <div className={styles.editField}>
                  <label className={styles.editLabel} htmlFor="campaign-public-page-url">
                    Endereço do site externo
                    <span className={styles.editLabelHint}>
                      (Link com https:// que substituirá a página da campanha)
                    </span>
                  </label>
                  <input
                    id="campaign-public-page-url"
                    type="url"
                    className={styles.editInput}
                    placeholder="www.exemplo.com/minha-campanha"
                    value={form.public_page_url ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, public_page_url: e.target.value }))
                    }
                    onBlur={(e) => {
                      const normalized = normalizeHttpsUrl(e.target.value)
                      setForm((prev) => ({ ...prev, public_page_url: normalized }))
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </ContainerTabPanel>
    </>
  )
}
